/**
 * Open an externally-produced URL in a new tab while surviving popup blockers.
 *
 * Calling `window.open` *after* an `await` loses the transient user activation
 * from the click, so browsers (especially mobile / in-app webviews) silently
 * block the popup and the button appears to do nothing. To avoid that we open a
 * blank tab synchronously inside the click gesture and only redirect it to the
 * real URL once `produceUrl` resolves. If the popup was blocked anyway (no tab
 * handle), we fall back to navigating the current tab. On failure the stub tab
 * is closed so the user isn't left staring at a blank page.
 */
export async function openLinkInNewTab(
  produceUrl: () => Promise<string>,
): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (tab) {
    // Sever the opener reference so the external page can't drive our window.
    tab.opener = null;
  }

  try {
    const url = await produceUrl();
    if (tab) {
      tab.location.replace(url);
    } else {
      window.location.href = url;
    }
  } catch {
    // The mutation/producer already records its own error state; here we just
    // clean up the blank tab we optimistically opened.
    tab?.close();
  }
}
